import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { API_URL } from '../lib/utils';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Checkbox } from '../components/ui/checkbox';
import { ShieldCheck, RefreshCw, CheckCircle2, Flag, XCircle, Trash2, ExternalLink } from 'lucide-react';

const STATUSES = [
  { key: 'pending_review', label: 'Needs Review' },
  { key: 'misclassified', label: 'Misclassified' },
  { key: 'active', label: 'Correctly Classified' },
  { key: 'expired', label: 'Expired' },
  { key: 'draft', label: 'Draft' },
];

const AdminTabInternshipQC = () => {
  const [status, setStatus] = useState('pending_review');
  const [data, setData] = useState({ counts: {}, internships: [] });
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState([]);
  const [busy, setBusy] = useState(false);
  const [page, setPage] = useState(1);
  const limit = 50;

  const load = useCallback(async () => {
    setLoading(true); setSelected([]);
    try {
      const res = await axios.get(`${API_URL}/admin/marketplace/internship-queue`, { params: { status, limit, page } });
      setData(res.data || { counts: {}, internships: [] });
    } catch (e) { toast.error('Could not load the QC queue.'); } finally { setLoading(false); }
  }, [status, page]);
  useEffect(() => { load(); }, [load]);
  useEffect(() => { setPage(1); }, [status]);

  const reaudit = async () => {
    setBusy(true);
    try {
      const res = await axios.post(`${API_URL}/admin/marketplace/reaudit-internships`);
      const r = res.data;
      toast.success(`Re-audit done: ${r.kept_active} kept, ${r.needs_review} flagged, ${r.misclassified} misclassified.`);
      await load();
    } catch (e) { toast.error('Re-audit failed.'); } finally { setBusy(false); }
  };

  const act = async (action, ids) => {
    if (!ids.length) { toast.error('Select at least one listing.'); return; }
    if (action === 'delete' && !window.confirm(`Permanently delete ${ids.length} listing(s)? They will be blocked from re-import.`)) return;
    setBusy(true);
    try {
      const res = await axios.post(`${API_URL}/admin/marketplace/internship-review`, { ids, action });
      toast.success(res.data.message);
      await load();
    } catch (e) { toast.error(e.response?.data?.detail || 'Action failed.'); } finally { setBusy(false); }
  };

  const toggle = (id) => setSelected((s) => s.includes(id) ? s.filter((x) => x !== id) : [...s, id]);
  const allIds = data.internships.map((i) => i.internship_id);
  const allSelected = allIds.length > 0 && selected.length === allIds.length;

  return (
    <div className="space-y-5" data-testid="admin-internship-qc">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div className="flex items-start gap-3">
          <ShieldCheck className="w-6 h-6 text-primary flex-shrink-0 mt-0.5" />
          <div>
            <h3 className="font-serif text-xl font-bold text-primary">Internship Quality Control</h3>
            <p className="text-gray-500 text-sm">Review imported listings, reclassify or remove professional jobs, and keep genuine student placements. Removed listings are blocked from re-import.</p>
          </div>
        </div>
        <Button size="sm" variant="outline" className="border-primary text-primary" onClick={reaudit} disabled={busy} data-testid="qc-reaudit-btn"><RefreshCw className={`w-4 h-4 mr-1 ${busy ? 'animate-spin' : ''}`} /> Re-audit Imports</Button>
      </div>

      {/* Status filter */}
      <div className="flex flex-wrap gap-2">
        {STATUSES.map((s) => (
          <button key={s.key} onClick={() => setStatus(s.key)}
            className={`px-3 py-1.5 rounded-full text-sm border transition-colors ${status === s.key ? 'bg-primary text-white border-primary' : 'bg-white text-gray-600 border-gray-200 hover:border-primary/40'}`}
            data-testid={`qc-filter-${s.key}`}>
            {s.label} <span className="opacity-70">({data.counts?.[s.key] ?? 0})</span>
          </button>
        ))}
      </div>

      {/* Bulk actions */}
      {selected.length > 0 && (
        <div className="flex flex-wrap items-center gap-2 bg-primary/5 border border-primary/15 rounded-xl p-3" data-testid="qc-bulk-bar">
          <span className="text-sm text-primary font-medium mr-2">{selected.length} selected</span>
          <Button size="sm" variant="outline" className="text-green-700 border-green-300" onClick={() => act('approve', selected)} disabled={busy} data-testid="qc-bulk-approve"><CheckCircle2 className="w-4 h-4 mr-1" /> Approve</Button>
          <Button size="sm" variant="outline" className="text-amber-700 border-amber-300" onClick={() => act('needs_review', selected)} disabled={busy} data-testid="qc-bulk-flag"><Flag className="w-4 h-4 mr-1" /> Flag</Button>
          <Button size="sm" variant="outline" className="text-red-600 border-red-300" onClick={() => act('misclassify', selected)} disabled={busy} data-testid="qc-bulk-misclassify"><XCircle className="w-4 h-4 mr-1" /> Misclassify</Button>
          <Button size="sm" variant="outline" className="text-red-700 border-red-400" onClick={() => act('delete', selected)} disabled={busy} data-testid="qc-bulk-delete"><Trash2 className="w-4 h-4 mr-1" /> Delete</Button>
        </div>
      )}

      {/* List */}
      {loading ? <div className="py-12 text-center text-gray-500">Loading…</div> : (
        <div className="bg-white rounded-2xl border overflow-hidden">
          <div className="flex items-center gap-3 px-4 py-2 border-b bg-gray-50 text-xs text-gray-500">
            <Checkbox checked={allSelected} onCheckedChange={() => setSelected(allSelected ? [] : allIds)} data-testid="qc-select-all" />
            <span>Select all on page</span>
          </div>
          {data.internships.length === 0 ? (
            <p className="py-10 text-center text-gray-400 text-sm">No listings in this status.</p>
          ) : data.internships.map((it) => (
            <div key={it.internship_id} className="flex items-start gap-3 px-4 py-3 border-b last:border-0" data-testid={`qc-row-${it.internship_id}`}>
              <Checkbox checked={selected.includes(it.internship_id)} onCheckedChange={() => toggle(it.internship_id)} data-testid={`qc-check-${it.internship_id}`} className="mt-1" />
              <div className="flex-1 min-w-0">
                <p className="font-medium text-primary text-sm">{it.title}</p>
                <p className="text-xs text-gray-500">{it.org_name || '—'}{it.location ? ` · ${it.location}` : ''}{it.source ? ` · ${it.source}` : ''}{it.degree_level ? ` · ${it.degree_level}` : ''}</p>
                {it.misclassified_reason && <p className="text-xs text-red-600 mt-0.5">{it.misclassified_reason}</p>}
              </div>
              <div className="flex items-center gap-2 flex-shrink-0">
                {it.external_url && <a href={it.external_url} target="_blank" rel="noopener noreferrer" className="text-gray-400 hover:text-primary" title="Open source"><ExternalLink className="w-4 h-4" /></a>}
                <Badge className="bg-gray-100 text-gray-600">{it.status}</Badge>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Pagination */}
      {(data.counts?.[status] ?? 0) > limit && (
        <div className="flex items-center justify-center gap-3" data-testid="qc-pagination">
          <Button size="sm" variant="outline" disabled={page <= 1 || loading} onClick={() => setPage((p) => Math.max(1, p - 1))} data-testid="qc-prev">Previous</Button>
          <span className="text-sm text-gray-500">Page {page} of {Math.max(1, Math.ceil((data.counts?.[status] ?? 0) / limit))}</span>
          <Button size="sm" variant="outline" disabled={page >= Math.ceil((data.counts?.[status] ?? 0) / limit) || loading} onClick={() => setPage((p) => p + 1)} data-testid="qc-next">Next</Button>
        </div>
      )}
    </div>
  );
};

export default AdminTabInternshipQC;
